import React, { useState } from "react";
import AOS from "aos";
import Menu from "../components/Menu";
import Footer from "../components/Footer";
import Preloader from "../components/sections/Preloader";
import ScrollTop from "../components/sections/ScrollTop";
import "../styles/setting.css";

const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:3001";

const Setting = () => {
  const [activeTab, setActiveTab] = useState("excel"); // 'excel', 'manual', 'mission', or 'export'
  const [exportType, setExportType] = useState("users"); // 'users', 'missions', or 'users-missions'
  const [uploadCode, setUploadCode] = useState("");
  const [excelFile, setExcelFile] = useState(null);
  const [manualAddresses, setManualAddresses] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  // Mission form state
  const [missionForm, setMissionForm] = useState({
    title: "",
    description: "",
    action: "like",
    reward: "",
    twitterUrl: "",
    tweetId: "",
  });

  React.useEffect(() => {
    AOS.init({
      duration: 2000,
      once: true,
    });
  }, []);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setExcelFile(file);
      setError(null);
    }
  };

  const handleExcelUpload = async () => {
    if (!uploadCode) {
      setError("Please enter upload code");
      return;
    }

    if (!excelFile) {
      setError("Please select an Excel file");
      return;
    }

    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const formData = new FormData();
      formData.append("file", excelFile);
      formData.append("code", uploadCode);

      const response = await fetch(
        `${API_BASE_URL}/api/whitelist/upload/excel`,
        {
          method: "POST",
          body: formData,
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Upload failed");
      }

      setResult({
        success: true,
        message: data.message,
        data: data.data,
      });
      setExcelFile(null);
      setUploadCode("");
      // Reset file input
      document.getElementById("excel-file").value = "";
    } catch (err) {
      setError(err.message || "Failed to upload whitelist");
    } finally {
      setLoading(false);
    }
  };

  const handleManualUpload = async () => {
    if (!uploadCode) {
      setError("Please enter upload code");
      return;
    }

    if (!manualAddresses.trim()) {
      setError("Please enter wallet addresses");
      return;
    }

    setLoading(true);
    setError(null);
    setResult(null);

    try {
      // Parse addresses from text area (split by newline, comma, or space)
      const addresses = manualAddresses
        .split(/[\n,\s]+/)
        .map((addr) => addr.trim())
        .filter((addr) => addr.length > 0);

      if (addresses.length === 0) {
        throw new Error("No valid addresses found");
      }

      const response = await fetch(
        `${API_BASE_URL}/api/whitelist/upload/manual`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            code: uploadCode,
            addresses: addresses,
          }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Upload failed");
      }

      setResult({
        success: true,
        message: data.message,
        data: data.data,
      });
      setManualAddresses("");
      setUploadCode("");
    } catch (err) {
      setError(err.message || "Failed to upload whitelist");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateMission = async () => {
    if (!uploadCode) {
      setError("Please enter upload code");
      return;
    }

    if (!missionForm.title || !missionForm.description || !missionForm.reward) {
      setError("Please fill in all required fields");
      return;
    }

    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const response = await fetch(`${API_BASE_URL}/api/mission`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          code: uploadCode,
          ...missionForm,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Failed to create mission");
      }

      setResult({
        success: true,
        message: "Mission created successfully",
        data: data.data,
      });
      setMissionForm({
        title: "",
        description: "",
        action: "like",
        reward: "",
        twitterUrl: "",
        tweetId: "",
      });
      setUploadCode("");
    } catch (err) {
      setError(err.message || "Failed to create mission");
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    if (!uploadCode) {
      setError("Please enter export code");
      return;
    }

    setLoading(true);
    setError(null);
    setResult(null);

    try {
      let endpoint = "";
      let filename = "";

      switch (exportType) {
        case "users":
          endpoint = "/api/export/users";
          filename = `users-with-missions-${
            new Date().toISOString().split("T")[0]
          }.xlsx`;
          break;
        case "missions":
          endpoint = "/api/export/missions";
          filename = `missions-with-users-${
            new Date().toISOString().split("T")[0]
          }.xlsx`;
          break;
        case "users-missions":
          endpoint = "/api/export/users-missions";
          filename = `users-and-missions-${
            new Date().toISOString().split("T")[0]
          }.xlsx`;
          break;
        default:
          throw new Error("Invalid export type");
      }

      const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ code: uploadCode }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Export failed");
      }

      // Get the blob from response
      const blob = await response.blob();

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      setResult({
        success: true,
        message: "Export completed successfully! File downloaded.",
      });
      setUploadCode("");
    } catch (err) {
      setError(err.message || "Failed to export data");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="wrapper">
      <Preloader />
      <Menu />

      <section
        className="setting"
        style={{
          backgroundImage: `url(${process.env.PUBLIC_URL}/images/background/bg-banner.png)`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          backgroundAttachment: "fixed",
          position: "relative",
        }}
      >
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(7, 10, 41, 0.85)",
            zIndex: 0,
          }}
        />
        <div className="container" style={{ position: "relative", zIndex: 1 }}>
          <div className="row">
            <div className="col-12">
              <div className="block-text center">
                <h6 className="sub-heading">
                  <span>Settings</span>
                </h6>
                <h3 className="heading" data-aos="fade-up">
                  Whitelist Management
                </h3>
                <p className="mb-30">
                  Upload whitelist addresses via Excel file or manual input
                </p>
              </div>

              <div className="setting-card">
                {/* Tab Navigation */}
                <div className="tab-navigation">
                  <button
                    className={`tab-btn ${
                      activeTab === "excel" ? "active" : ""
                    }`}
                    onClick={() => setActiveTab("excel")}
                  >
                    Upload Excel
                  </button>
                  <button
                    className={`tab-btn ${
                      activeTab === "manual" ? "active" : ""
                    }`}
                    onClick={() => setActiveTab("manual")}
                  >
                    Manual Input
                  </button>
                  <button
                    className={`tab-btn ${
                      activeTab === "mission" ? "active" : ""
                    }`}
                    onClick={() => setActiveTab("mission")}
                  >
                    Create Mission
                  </button>
                  <button
                    className={`tab-btn ${
                      activeTab === "export" ? "active" : ""
                    }`}
                    onClick={() => setActiveTab("export")}
                  >
                    Export Data
                  </button>
                </div>

                {/* Upload Code Input */}
                <div className="code-input-group">
                  <label>
                    {activeTab === "export" ? "Export Code" : "Upload Code"}
                  </label>
                  <input
                    type="password"
                    placeholder={
                      activeTab === "export"
                        ? "Enter export code"
                        : "Enter upload code"
                    }
                    value={uploadCode}
                    onChange={(e) => setUploadCode(e.target.value)}
                    className="code-input"
                  />
                </div>

                {/* Excel Upload Tab */}
                {activeTab === "excel" && (
                  <div className="upload-section">
                    <div className="file-input-group">
                      <label>Select Excel File (.xlsx, .xls)</label>
                      <input
                        id="excel-file"
                        type="file"
                        accept=".xlsx,.xls"
                        onChange={handleFileChange}
                        className="file-input"
                      />
                      {excelFile && (
                        <div className="file-info">
                          Selected: {excelFile.name} (
                          {(excelFile.size / 1024).toFixed(2)} KB)
                        </div>
                      )}
                    </div>
                    <button
                      onClick={handleExcelUpload}
                      disabled={loading}
                      className="action-btn"
                    >
                      {loading ? "Uploading..." : "Upload Excel"}
                    </button>
                  </div>
                )}

                {/* Manual Input Tab */}
                {activeTab === "manual" && (
                  <div className="upload-section">
                    <div className="textarea-group">
                      <label>
                        Wallet Addresses (one per line, or separated by comma)
                      </label>
                      <textarea
                        placeholder="0x1234567890123456789012345678901234567890&#10;0xabcdefabcdefabcdefabcdefabcdefabcdefabcd&#10;..."
                        value={manualAddresses}
                        onChange={(e) => setManualAddresses(e.target.value)}
                        className="addresses-textarea"
                        rows={10}
                      />
                    </div>
                    <button
                      onClick={handleManualUpload}
                      disabled={loading}
                      className="action-btn"
                    >
                      {loading ? "Uploading..." : "Upload Addresses"}
                    </button>
                  </div>
                )}

                {/* Create Mission Tab */}
                {activeTab === "mission" && (
                  <div className="upload-section">
                    <div className="form-group">
                      <label>Mission Title *</label>
                      <input
                        type="text"
                        placeholder="e.g., Like Twitter Post"
                        value={missionForm.title}
                        onChange={(e) =>
                          setMissionForm({
                            ...missionForm,
                            title: e.target.value,
                          })
                        }
                        className="code-input"
                      />
                    </div>
                    <div className="form-group">
                      <label>Description *</label>
                      <textarea
                        placeholder="Mission description..."
                        value={missionForm.description}
                        onChange={(e) =>
                          setMissionForm({
                            ...missionForm,
                            description: e.target.value,
                          })
                        }
                        className="addresses-textarea"
                        rows={4}
                      />
                    </div>
                    <div className="form-group">
                      <label>Action *</label>
                      <select
                        value={missionForm.action}
                        onChange={(e) =>
                          setMissionForm({
                            ...missionForm,
                            action: e.target.value,
                          })
                        }
                        className="code-input"
                      >
                        <option value="like">Like</option>
                        <option value="retweet">Retweet</option>
                        <option value="follow">Follow</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Reward *</label>
                      <input
                        type="text"
                        placeholder="e.g., 50 $CVAI"
                        value={missionForm.reward}
                        onChange={(e) =>
                          setMissionForm({
                            ...missionForm,
                            reward: e.target.value,
                          })
                        }
                        className="code-input"
                      />
                    </div>
                    <div className="form-group">
                      <label>Twitter URL</label>
                      <input
                        type="text"
                        placeholder="https://twitter.com/CVMechNFT"
                        value={missionForm.twitterUrl}
                        onChange={(e) =>
                          setMissionForm({
                            ...missionForm,
                            twitterUrl: e.target.value,
                          })
                        }
                        className="code-input"
                      />
                    </div>
                    <div className="form-group">
                      <label>Tweet ID</label>
                      <input
                        type="text"
                        placeholder="1234567890"
                        value={missionForm.tweetId}
                        onChange={(e) =>
                          setMissionForm({
                            ...missionForm,
                            tweetId: e.target.value,
                          })
                        }
                        className="code-input"
                      />
                    </div>
                    <button
                      onClick={handleCreateMission}
                      disabled={loading}
                      className="action-btn"
                    >
                      {loading ? "Creating..." : "Create Mission"}
                    </button>
                  </div>
                )}

                {/* Export Tab */}
                {activeTab === "export" && (
                  <div className="upload-section">
                    <div className="form-group">
                      <label>Export Type *</label>
                      <select
                        value={exportType}
                        onChange={(e) => setExportType(e.target.value)}
                        className="code-input"
                      >
                        <option value="users">
                          Users with Missions (List of unique users)
                        </option>
                        <option value="missions">
                          Missions with Users (One sheet per mission)
                        </option>
                        <option value="users-missions">
                          Users & Missions (Detailed list)
                        </option>
                      </select>
                    </div>
                    <div className="form-group">
                      <p
                        style={{
                          color: "rgba(255, 255, 255, 0.7)",
                          fontSize: "14px",
                          marginTop: "0.5rem",
                        }}
                      >
                        {exportType === "users" &&
                          "Export a list of all unique wallet addresses that have completed at least one mission."}
                        {exportType === "missions" &&
                          "Export missions with their completed users. Each mission will have its own sheet in the Excel file."}
                        {exportType === "users-missions" &&
                          "Export a detailed list showing each user and all missions they have completed."}
                      </p>
                    </div>
                    <button
                      onClick={handleExport}
                      disabled={loading}
                      className="action-btn"
                    >
                      {loading ? "Exporting..." : "Export Excel"}
                    </button>
                  </div>
                )}

                {/* Error Message */}
                {error && (
                  <div className="error-message">
                    <span>✗</span> {error}
                  </div>
                )}

                {/* Success Result */}
                {result && result.success && (
                  <div className="success-message">
                    <span>✓</span> {result.message}
                    {result.data && (
                      <div className="result-details">
                        <p>Success: {result.data.success} addresses</p>
                        {result.data.failed > 0 && (
                          <p>Failed: {result.data.failed} addresses</p>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      <ScrollTop />
    </div>
  );
};

export default Setting;
